import { type SubjectWithLessons } from "@core/grades/types";
import { trans } from "@mongez/localization";
import {
  ArrowDownIcon,
  BookMarkedIcon,
  BookOpenIcon,
  FolderTreeIcon,
  SparklesIcon,
} from "lucide-react";

type SubjectDetailsHeaderProps = {
  subject: SubjectWithLessons;
  totalCategories: number;
  totalLessons: number;
};

export function SubjectDetailsHeader({
  subject,
  totalCategories,
  totalLessons,
}: SubjectDetailsHeaderProps) {
  return (
    <header className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-gray-950 px-5 py-7 text-white shadow-2xl shadow-primary-950/10 dark:border-gray-800 sm:px-8 sm:py-9 lg:px-10">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(99,102,241,.55),transparent_32%),radial-gradient(circle_at_85%_85%,rgba(6,182,212,.35),transparent_35%)]" />
      <div className="pointer-events-none absolute -end-16 -top-20 h-64 w-64 rounded-full border-[42px] border-white/5" />
      <div className="pointer-events-none absolute bottom-0 start-1/3 h-px w-1/2 bg-gradient-to-r from-transparent via-cyan-300/70 to-transparent" />

      <div className="relative z-10 grid items-center gap-7 lg:grid-cols-[1fr_auto] lg:gap-12">
        <div className="flex min-w-0 flex-col items-center text-center sm:items-start sm:text-start">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-cyan-100 backdrop-blur">
            <SparklesIcon className="h-3.5 w-3.5" />
            {trans("grades.subjectJourney")}
          </span>

          <h1 className="text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl">
            {subject.name}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-gray-300 sm:text-base sm:leading-8">
            {subject.description ||
              trans("grades.subjectDescription", { subject: subject.name })}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2 sm:justify-start">
            <span className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-3.5 py-2 text-sm font-bold text-white backdrop-blur">
              <BookOpenIcon className="h-4 w-4 text-cyan-300" />
              {trans("grades.lessonsCount", { count: totalLessons })}
            </span>
            <span className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-3.5 py-2 text-sm font-bold text-white backdrop-blur">
              <FolderTreeIcon className="h-4 w-4 text-violet-300" />
              {trans("grades.categoriesCount", { count: totalCategories })}
            </span>
          </div>

          <a
            href="#subject-lessons"
            className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-extrabold text-gray-950 shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-gray-950"
          >
            {trans("grades.startExploring")}
            <ArrowDownIcon className="h-4 w-4" />
          </a>
        </div>

        <div className="relative mx-auto flex h-48 w-48 shrink-0 items-center justify-center sm:h-56 sm:w-56 lg:mx-0">
          <div className="absolute inset-0 rotate-6 rounded-[2.5rem] bg-gradient-to-br from-violet-500 to-cyan-400 opacity-70" />
          <div className="absolute inset-2 -rotate-3 rounded-[2.25rem] border border-white/20 bg-white/10 backdrop-blur" />
          <div className="relative flex h-40 w-40 items-center justify-center overflow-hidden rounded-[2rem] bg-white/95 shadow-2xl sm:h-48 sm:w-48">
            {subject.image?.url ? (
              <img
                src={subject.image.url}
                alt={subject.name}
                className="h-full w-full object-cover transition duration-500 hover:scale-105"
              />
            ) : (
              <BookMarkedIcon className="h-16 w-16 text-primary-600 sm:h-20 sm:w-20" />
            )}
          </div>
          <span className="absolute -bottom-2 -start-3 rounded-2xl border border-white/20 bg-gray-900/85 px-4 py-2 text-xs font-bold text-cyan-200 shadow-xl backdrop-blur">
            {trans("grades.readyToLearn")}
          </span>
        </div>
      </div>
    </header>
  );
}
